import { Developer } from '@crazygames/game-types/src/developer/developer.type';
import { gql } from '@apollo/client';
import { GAME_BUSINESS_TIER, GameCoverWithUploadedFile } from '../../../../../../common/graphql/games/game';
import { XsollaInfo } from '@crazygames/graph-types/xsolla/xsolla-info.type';
import { BASIC_LAUNCH_STATS_FRAGMENT, BasicLaunchAvgStatsWithDates } from '../../Submissions/submissions-graphql';
import { GAME_STATUS } from '@crazygames/game-types/src/game/game-status.type';
import { SUBMISSION_STATUS } from '@crazygames/game-types/src/submission/submission-status.type';
import { LegalFormStatus } from '@crazygames/game-types/src/submission/legal-form-status.type';
import { AssigneeType } from '@crazygames/game-types/src/submission/assignee.type';
import { SubmissionVideo } from '@crazygames/graph-types/submission/submission-video.type';
import { MultiplayerOptions } from '../../../UserDeveloper/Submission/GameBaseForm/baseFormHelper';

export interface BaseAdminSubmission {
  id: string;
  slug: string;
  gameCovers: GameCoverWithUploadedFile[] | null;
  gameName: string;
  createdAt: string;
  status: SUBMISSION_STATUS;
  developerId: string;
  isFullLaunch: boolean;
  basicLaunchStats: BasicLaunchAvgStatsWithDates | null;
  basicLaunchExpiresOn: string | null;
  statusChanges: SubmissionStatusChangesType;
}

/**
 * Submission interface for admin logic
 */
export interface AdminSubmission extends BaseAdminSubmission {
  /**
   * The slug of the corresponding game, or what the slug would be
   * If gameId is set this is the slug of the game, if gameId is not set this is the same as slugify(gameName)
   */
  gameStatus: GAME_STATUS;
  feedback: FeedbackType;
  internalNotes: InternalNotesType;
  developer: Pick<Developer, 'name'>; // based on the attributes queried in ADMIN_SUBMISSION_FRAGMENT
  gameId: string;
  categoryId: string;
  categoryName: string;
  tagIds: string[];
  description: string;
  controls: string;
  steamStoreLink: string;
  appStoreLink: string;
  playStoreLink: string;
  playStoreDownloads?: number;
  appStoreDownloads?: number;
  steamDownloads?: number;
  rawRevenueShareOptions?: string;
  proposedRevenueShare?: SubmissionRevenueShare;
  proposedNonExclusiveRevenueShare?: SubmissionRevenueShare;
  updateNotes?: UpdateNote[];
  filesChanged?: boolean;
  coverChanged?: boolean;
  iframeLinkChanged?: boolean;
  assignee?: AssigneeType;
  xsolla: XsollaInfo | null;
  businessAssigneeId?: string;
  businessTier?: GAME_BUSINESS_TIER;
  shareEmail: boolean;
  videos: SubmissionVideo | null;
  legalFormStatus: LegalFormStatus;
  skipFileChecks?: boolean;
  mobileFriendly: boolean;
  multiplayerOptions?: MultiplayerOptions;
}

export type SubmissionRevenueShare = {
  ingameShare: number;
  prerollShare: number;
  outgameShare: number;
  iapShare: number;
};

export type FeedbackType = Review[];
export type InternalNotesType = Review[];
export type SubmissionStatusChangesType = SubmissionStatusChange[];

export interface Review {
  comment: string;
  postedOn: string;
  authorName: string;
}

export interface SubmissionStatusChange {
  status: SUBMISSION_STATUS;
  changedOn: string;
  authorName: string;
  isFullLaunch?: boolean;
}

export interface GetSubmissionQuery {
  submission: AdminSubmission;
}

export interface UpdateNote {
  createdAt: string;
  text: string;
}

export const ADMIN_SUBMISSION_FRAGMENT = gql`
  fragment AdminSubmissionFragment on Submission {
    id
    developerId
    developer {
      name
    }
    gameId
    gameName
    gameStatus
    slug
    categoryId
    tagIds
    description
    controls
    steamStoreLink
    appStoreLink
    playStoreDownloads
    appStoreDownloads
    steamDownloads
    playStoreLink
    status
    gameCovers {
      size
      uploadedFile {
        uploadId
        path
        size
        temporaryUrl
      }
    }
    rawRevenueShareOptions
    proposedRevenueShare {
      ingameShare
      prerollShare
      outgameShare
      iapShare
    }
    proposedNonExclusiveRevenueShare {
      ingameShare
      prerollShare
      outgameShare
      iapShare
    }
    feedback {
      comment
      postedOn
      authorName
    }
    internalNotes {
      comment
      postedOn
      authorName
    }
    statusChanges {
      status
      changedOn
      authorName
      isFullLaunch
    }
    createdAt
    updateNotes {
      createdAt
      text
    }
    filesChanged
    coverChanged
    iframeLinkChanged
    assignee {
      id
      name
    }
    businessAssigneeId
    shareEmail
    multiplayerOptions {
      isMultiplayer
      supportsInstantJoin
      hasModeratedChat
      minLobbySize
      maxLobbySize
    }
    videos {
      videoThumbnail
      videoOriginal
      portraitVideoThumbnail
      portraitVideoOriginal
    }
    xsolla {
      gameProjectId
      credentials {
        projectId
        clientId
        clientSecret
      }
    }
    isFullLaunch
    basicLaunchStats {
      ...BasicLaunchStats
    }
    basicLaunchExpiresOn
    legalFormStatus
    skipFileChecks
    mobileFriendly
  }
  ${BASIC_LAUNCH_STATS_FRAGMENT}
`;

export const GET_SUBMISSION_QUERY = gql`
  query getSubmission($id: String!) {
    submission(id: $id) {
      ...AdminSubmissionFragment
    }
  }
  ${ADMIN_SUBMISSION_FRAGMENT}
`;
